import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { getIsExportingGrid } from "@icarius-app/selectors";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { openDialogAction } from "@icarius-common/dialog/actions";
import {
    getAssistanceAction,
    getMarksForMapAction,
    deleteAssistanceMarkAction,
    getChangeScheduleAssistanceAction,
    updateChangeScheduleAssistanceAction,
    deleteDuplicatedAssistanceMarkAction,
} from "../actions";
import {
    getAssistanceData,
    getDateFormat,
    getLocale,
    getIsLoading,
    getMapData,
    getOpenCalendarList,
    getEmployeeList,
    getSchedules,
    getHasDuplicatedMarks,
} from "../selectors";
import { createDateFromDDMMYYYY, formatDateYYYYMMDD, getDateDayName } from "@icarius-utils/date";

const useAssistance = () => {
    const [showAllRows, setshowAllRows] = useState(true);
    const [selectedData, setSelectedData] = useState(null);
    const [assistanceMarkDialogIsOpen, setAssistanceMarkDialogIsOpen] = useState(false);
    const [changeScheduleDialogIsOpen, setChangeScheduleDialogIsOpen] = useState(false);
    const [mapIsOpen, setMapIsOpen] = useState(false);

    const dispatch = useDispatch();

    const handleDeleteDuplicatedAssistanceMarks = useCallback(() => {
        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("deleteDuplicatedMarksQuestion"),
                onConfirm: () => dispatch(deleteDuplicatedAssistanceMarkAction()),
            })
        );
    }, [dispatch]);

    useEffect(() => {
        dispatch(getAssistanceAction()).then((resp) => {
            if (resp.status === 200) {
                if (resp.data?.hasDuplicatedMarks) {
                    handleDeleteDuplicatedAssistanceMarks();
                }
            }
        });
    }, [dispatch, handleDeleteDuplicatedAssistanceMarks]);

    const isLoading = useSelector(getIsLoading);
    const isExportingGrid = useSelector(getIsExportingGrid);
    const hasDuplicatedMarks = useSelector(getHasDuplicatedMarks);
    const assistanceData = useSelector(getAssistanceData);
    const openCalendarList = useSelector(getOpenCalendarList);
    const employeeList = useSelector(getEmployeeList);
    const mapData = useSelector(getMapData);
    const schedules = useSelector(getSchedules);
    const dateFormat = useSelector(getDateFormat);
    const locale = useSelector(getLocale);

    const rowData = useMemo(() => {
        if (showAllRows) return assistanceData;
        return assistanceData.filter((row) => !row["FECHA DE ENTRADA"] || !row["FECHA DE SALIDA"]);
    }, [showAllRows, assistanceData]);

    const handleToggleShowAllRows = () => {
        setshowAllRows((prev) => !prev);
    };

    const handleOpenChangeSchedule = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const selectedRow = selectedRows[0];
        const dateToUse = selectedRow["FECHA DE ENTRADA"] || selectedRow["FECHA DE SALIDA"];

        const newSelectedData = {
            code: selectedRow["CODIGO DE EMPLEADO"],
            schedule: selectedRow["HORARIO"],
            date: formatDateYYYYMMDD(createDateFromDDMMYYYY(dateToUse)),
            day: getDateDayName(dateToUse),
        };

        dispatch(getChangeScheduleAssistanceAction(newSelectedData.code, newSelectedData.date)).then((resp) => {
            if (resp.status === 200) {
                setSelectedData(newSelectedData);
                setChangeScheduleDialogIsOpen(true);
            }
        });
    };

    const handleCloseChangeSchedule = () => {
        setChangeScheduleDialogIsOpen(false);
        setSelectedData(null);
    };

    const handleDeleteAssistanceMark = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const selectedRow = selectedRows[0];

        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("deleteMarkQuestion", {
                    name: selectedRow["APELLIDO Y NOMBRE"],
                    date: selectedRow["FECHA DE ENTRADA"],
                }),
                onConfirm: () => dispatch(deleteAssistanceMarkAction({ internalCode: selectedRow.internalCode })),
            })
        );
    };

    const handleOpenCreateAssistanceMark = () => {
        setSelectedData(null);
        setAssistanceMarkDialogIsOpen(true);
    };

    const handleOpenEditAssistanceMark = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        // validar que el empleado tenga calendario abierto
        const rowEmployee = employeeList?.find((item) => item.employeeCode === selectedRows[0]["CODIGO DE EMPLEADO"]);

        if (!openCalendarList?.some((item) => item.type === rowEmployee.calendarType)) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("cantEditEmployeeHasNoOpenCalendar", {
                        name: rowEmployee?.employeeName || getLocalizedString("theEmployee"),
                    }),
                    severity: "error",
                })
            );
            return;
        }

        setAssistanceMarkDialogIsOpen(true);
        setSelectedData(selectedRows[0]);
    };

    const handleCloseAssistanceMark = () => {
        setAssistanceMarkDialogIsOpen(false);
        setSelectedData(null);
    };

    const handleOpenMap = (gridRef) => {
        let mapDataToUse = [];

        if (mapIsOpen) {
            setMapIsOpen(false);
            return;
        }

        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length === 0) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRecord"), severity: "warning" })
            );
            return;
        }

        if (selectedRows.some((row) => !row.hasEntryGPSCoordinates && !row.hasExitGPSCoordinates)) {
            dispatch(
                openDialogAction({
                    title: getLocalizedString("atention"),
                    msg: getLocalizedString("noGeoPositionInAttendanceMarkToShowOnMap"),
                    acceptOnly: true,
                })
            );
            return;
        }

        mapDataToUse = selectedRows
            .map((row) => ({
                employeeCode: row["CODIGO DE EMPLEADO"],
                entryDate: row["FECHA DE ENTRADA"],
                exitDate: row["FECHA DE SALIDA"],
            }))
            .filter((row) => row.employeeCode && (row.entryDate || row.exitDate));

        if (!mapDataToUse.length) {
            dispatch(
                openDialogAction({
                    title: getLocalizedString("atention"),
                    msg: getLocalizedString("attendanceMarksRequireEntryOrExit"),
                    acceptOnly: true,
                })
            );
            return;
        }

        dispatch(getMarksForMapAction(mapDataToUse)).then((resp) => {
            if (resp?.status === 200) {
                setMapIsOpen(true);
            }
        });
    };

    const handleCloseMap = () => {
        setMapIsOpen(false);
    };

    const handleSubmitScheduleChange = (dataToSend) => {
        dispatch(updateChangeScheduleAssistanceAction(dataToSend)).then((res) => {
            if (res?.status === 200) {
                dispatch(getAssistanceAction());
                handleCloseChangeSchedule();
            }
        });
    };

    const state = {
        showAllRows,
        selectedData,
        assistanceMarkDialogIsOpen,
        changeScheduleDialogIsOpen,
        mapIsOpen,
    };

    const handlers = {
        handleToggleShowAllRows,
        handleOpenCreateAssistanceMark,
        handleOpenEditAssistanceMark,
        handleCloseAssistanceMark,
        handleDeleteAssistanceMark,
        handleDeleteDuplicatedAssistanceMarks,
        handleOpenChangeSchedule,
        handleCloseChangeSchedule,
        handleOpenMap,
        handleCloseMap,
        handleSubmitScheduleChange,
    };

    return {
        isLoading: isLoading || isExportingGrid,
        hasDuplicatedMarks,
        rowData,
        assistanceData,
        openCalendarList,
        employeeList,
        mapData,
        schedules,
        dateFormat,
        locale,
        state,
        handlers,
    };
};

export default useAssistance;
